import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { Form, Formik, Field } from 'formik';
import * as Yup from 'yup';

import {
    ICreateLtiModuleInput,
    IModuleWeight,
    IOpenOffsetType,
} from 'graphql/types';
import { Button } from 'common/components/Button';
import { LinkField, TextField } from 'common/components/FormField';
import { ExitPageAlert } from 'common/components/ExitPageAlert';
import { UnsavedChangesAlert } from 'common/components/Alerts';
import { ContentExpander } from 'common/components/ContentExpander';
import { Typography } from 'common/components/Typography';
import { IModuleFormValues } from 'training/types';
import { ModuleImageFormSection } from 'training/components/ModuleImageFormSection';
import { ModuleOffsetFormSection } from 'training/components/ModuleOffsetFormSection';
import { FormDeleteButton } from 'common/components/Button/FormDeleteButton';
import { FormSection } from 'common/components/FormSection';
import { Link } from 'common/components/Link';
import { ModuleWeightFormSection } from 'training/components/ModuleWeightFormSection';

export interface ILTIModuleFormValues
    extends
        IModuleFormValues,
        Omit<ICreateLtiModuleInput, 'imageId' | 'offset'> {}

export interface IProps {
    initialValues?: Partial<ILTIModuleFormValues>;
    disabled?: boolean;
    edit?: boolean;
    onClose?(): void;
    onSubmit(values: ILTIModuleFormValues): void;
    onDelete?(): void;
    moduleGroupId?: string;
}

export const LTIModuleForm = ({
    initialValues,
    disabled,
    edit,
    moduleGroupId,
    onClose,
    onDelete,
    ...other
}: IProps) => {
    const [translate] = useTranslation();
    const [settingsExpanded, setSettingsExpanded] = useState(false);

    const validationSchema = Yup.object().shape({
        title: Yup.string().required(translate('validate.required')),
        ltiUrl: Yup.string().required(translate('validate.required')),
    });

    const initValues: ILTIModuleFormValues = {
        title: '',
        ltiUrl: '',
        ltiKey: '',
        ltiSecret: '',
        ltiParameters: '',
        weight: IModuleWeight.Weight1,
        offset: {
            type: IOpenOffsetType.Zero,
            days: 0,
            hours: 0,
        },
        ...initialValues,
    };

    const formTitle = edit
        ? translate('moduleForm.lti.editTitle')
        : translate('moduleForm.lti.newTitle');
    const formText = edit
        ? translate('moduleForm.lti.editText')
        : translate('moduleForm.lti.newText');
    const submitText = edit
        ? translate('moduleForm.lti.editButton')
        : translate('moduleForm.lti.newButton');

    return (
        <Formik
            initialValues={initValues}
            validationSchema={validationSchema}
            {...other}
        >
            {({ submitForm, isSubmitting, dirty, values, errors }) => (
                <Box sx={{ p: { xs: 2, sm: 4 } }}>
                    <ExitPageAlert
                        alert={UnsavedChangesAlert}
                        when={dirty && !isSubmitting}
                        onConfirm={submitForm}
                    />
                    <Form>
                        {formTitle && formText && (
                            <Box sx={{ mb: 4 }}>
                                {formTitle && (
                                    <Typography
                                        sx={{ fontWeight: 'bold' }}
                                        variant="h3"
                                    >
                                        {formTitle}
                                    </Typography>
                                )}

                                {formText && (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography>{formText}</Typography>
                                    </Box>
                                )}
                            </Box>
                        )}

                        <Field
                            component={TextField}
                            label={translate('title')}
                            name="title"
                        />

                        <Field
                            multiline
                            component={TextField}
                            label={translate('description')}
                            name="description"
                        />

                        <Box sx={{ mt: 4 }}>
                            <ContentExpander
                                expand={settingsExpanded}
                                title={translate('moreSettings')}
                                onClick={() =>
                                    setSettingsExpanded(!settingsExpanded)
                                }
                            >
                                <FormSection
                                    description={
                                        <Trans
                                            components={{
                                                a: (
                                                    <Link
                                                        rel="noreferrer"
                                                        underline="always"
                                                    />
                                                ),
                                            }}
                                            i18nKey="moduleForm.lti.ltiInfoDescription"
                                        />
                                    }
                                    hasError={!!errors?.ltiUrl}
                                    title={translate('moduleForm.lti.ltiInfo')}
                                >
                                    <LinkField
                                        label={translate(
                                            'moduleForm.lti.ltiUrl'
                                        )}
                                        name="ltiUrl"
                                    />

                                    <Field
                                        component={TextField}
                                        label={translate(
                                            'moduleForm.lti.ltiKey'
                                        )}
                                        name="ltiKey"
                                    />

                                    <Field
                                        component={TextField}
                                        label={translate(
                                            'moduleForm.lti.ltiSecret'
                                        )}
                                        name="ltiSecret"
                                    />

                                    <Field
                                        multiline
                                        component={TextField}
                                        label={translate(
                                            'moduleForm.lti.ltiParams'
                                        )}
                                        name="ltiParameters"
                                    />
                                </FormSection>

                                <ModuleWeightFormSection />

                                <ModuleImageFormSection />
                                {/* Only show conditions section when we have a module group id */}
                                {moduleGroupId && (
                                    <ModuleOffsetFormSection
                                        moduleGroupId={moduleGroupId}
                                        offsetValues={
                                            values.offset || undefined
                                        }
                                    />
                                )}
                            </ContentExpander>
                        </Box>

                        <Box sx={{ display: 'flex', mt: 4 }}>
                            <Box>
                                <Button
                                    color="primary"
                                    disabled={
                                        isSubmitting ||
                                        disabled ||
                                        (!isSubmitting && !disabled && !dirty)
                                    }
                                    loading={isSubmitting}
                                    type="submit"
                                    variant="contained"
                                    onClick={(
                                        e: React.MouseEvent<HTMLButtonElement>
                                    ) => {
                                        e.preventDefault();

                                        if (!!errors.ltiUrl) {
                                            setSettingsExpanded(true);
                                        }

                                        return submitForm();
                                    }}
                                >
                                    {submitText}
                                </Button>
                            </Box>
                            {onClose && (
                                <Box sx={{ ml: 2 }}>
                                    <Button
                                        disabled={isSubmitting}
                                        variant="contained"
                                        onClick={onClose}
                                    >
                                        {translate('cancel')}
                                    </Button>
                                </Box>
                            )}
                            {onDelete && (
                                <Box sx={{ ml: 'auto' }}>
                                    <FormDeleteButton
                                        disabled={isSubmitting || disabled}
                                        onClick={onDelete}
                                    >
                                        {translate('delete')}
                                    </FormDeleteButton>
                                </Box>
                            )}
                        </Box>
                    </Form>
                </Box>
            )}
        </Formik>
    );
};
